<template>
    <div class="table-header" v-sticky="{ zIndex: 1000, stickyTop: 63, disabled: false}">
        <b-row align-v="center" align-h="between">
            <b-col class="table-header-actions">
                <transition name="fade">
                    <b-button @click="toTop"
                              v-if="topButton"
                              size="sm"
                              class="mr-1"
                              :disabled="!toTopActive"
                              v-b-tooltip.hover.bottom
                              title="Подняться в начало"
                              variant="light">
                        <i class="fas fa-arrow-alt-up"></i>
                    </b-button>
                </transition>
                <slot name="beforeActions"></slot>
                <table-header-actions-toolbar :actions="actionsToolbar"
                                              :window-width="windowWidth"
                                              :show="showActionsToolbar"
                                              :collapse-count="!!$slots.beforeActions ? 2 : 3"
                                              @action="doAction"/>
                <slot name="afterActions"></slot>
            </b-col>
            <b-col cols="auto" v-if="totalRows">
                <slot name="beforePagination"></slot>
                <fup-pagination
                        class="m-0"
                        size="sm"
                        :total-rows="totalRows"
                        :minimize="windowWidth < 1366"
                        :value="currentPage"
                        @input="changePage"
                        :hide-goto-end-buttons="windowWidth < 1024"
                        :per-page="perPageData"></fup-pagination>
                <slot name="afterPagination"></slot>
            </b-col>
            <b-col cols="auto" :class="{'pl-0': totalRows}">
                <table-header-searcher v-if="typeof $listeners.search === 'function'"
                                       :minimize="windowWidth < 1366"
                                       :value="searchQuery"
                                       @input="setSearchQuery"
                                       @search="search"/>
                <b-dropdown v-if="perPageData || configurableTable"
                            :variant="isTableFieldsChanged ? 'warning' : 'light'" right class="m-0" size="sm"
                            :no-caret="windowWidth < 1366">
                    <template slot="button-content">
                        <i class="fas fa-cog"></i>
                    </template>
                    <b-dropdown-item-button @click="openTableConfigurator" v-if="configurableTable">
                        Настроить таблицу
                        <b-badge v-if="isTableFieldsChanged" variant="warning">Изменено</b-badge>
                    </b-dropdown-item-button>
                    <template v-if="perPageData">
                        <b-dropdown-divider v-if="configurableTable"></b-dropdown-divider>
                        <b-dropdown-item-button v-for="item in perPageVariants"
                                                :key="item.value"
                                                :active="item.value === perPageData"
                                                @click="perPageData = item.value">
                            {{item.text}}
                        </b-dropdown-item-button>
                    </template>
                    <b-dropdown-item-button @click="toggleTopButtonState">
                        {{topButton ? 'Выключить кнопку "Вверх"' : 'Включить кнопку "Вверх"'}}
                    </b-dropdown-item-button>
                </b-dropdown>

                <b-col cols="auto" v-if="$slots['buttons']">
                    <slot name="buttons"></slot>
                </b-col>

            </b-col>
        </b-row>
        <div class="labels" v-if="$slots['filters']">
            <b-row align-h="between">
                <b-col>
                    <slot name="filters"></slot>
                </b-col>
                <b-col cols="auto">
                    <b-button @click="discardFilters" size="sm" variant="warning">
                        <i class="fas fa-minus-hexagon"></i>
                        Очистить
                    </b-button>
                </b-col>
            </b-row>
        </div>
        <div class="labels" v-if="$slots.labels">
            <slot name="labels"></slot>
        </div>
        <portal to="app">
            <modal v-if="configurableTable"
                   name="table-configurator"
                   :width="480"
                   height="auto"
                   :scrollable="true">
                <fup-modal-constructor @close="closeTableConfigurator"
                                       title="Настройка таблицы"
                                       icon="wrench">
                    <template slot="default">
                        <template v-if="tableFields">
                            <h3>Колонки таблицы</h3>
                            <p>Вы можете отключить ненужные колонки и изменить их порядок</p>
                            <fup-draggable v-if="configuredTableFields && configuratorModal"
                                           tag="ul"
                                           v-model="configuredTableFields"
                                           class="list-group"
                                           handle=".handle"
                                           @change="saveTableConfig">
                                <li onselectstart="return false" class="list-group-item"
                                    v-for="(element, idx) in configuredTableFields" :key="element.key">
                                    <b-form-checkbox v-model="element.show" switch
                                                     @input="saveTableConfig"
                                                     :disabled="configuredTableFieldsActive && element.show">
                                        <i class="far fa-sort text-muted mr-2 handle"></i> {{
                                        element.label }}
                                    </b-form-checkbox>
                                </li>
                            </fup-draggable>
                        </template>
                    </template>
                    <template slot="footer">
                        <div class="actions">
                            <b-button @click="resetTableFields">Сбросить по умолчанию</b-button>
                            <b-button @click="closeTableConfigurator" variant="warning">Готово</b-button>
                        </div>
                    </template>
                </fup-modal-constructor>
            </modal>
        </portal>
    </div>

</template>

<script>
    import VueSticky from 'vue-sticky'
    import scrollToElement from "scroll-to-element"
    import TableHeaderSearcher from "./TableHeaderSearcher"
    import TableHeaderActionsToolbar from "./TableActionsToolbar"
    import filter from 'lodash/filter'

    export default {
        name: "TableHeader",
        components: {TableHeaderSearcher, TableHeaderActionsToolbar},
        directives: {
            'sticky': VueSticky,
        },
        props: {
            query: null,
            filtersText: {
                type: Array,
                default: () => {
                    return ['Показать фильтры', 'Скрыть фильтры']
                }
            },
            filtersIcon: {
                type: String,
                default: 'filter'
            },
            paginator: false,
            perPage: 0,
            totalRows: 0,
            currentPage: {
                type: Number,
                default: 1
            },
            searchQuery: {
                type: String,
                default: null
            },
            tableFields: {
                type: [Array, null],
                default: null
            },
            configurableTable: {
                type: Boolean,
                default: false
            },
            showActionsToolbar: null,
            actionsToolbar: null,
            perPageVariants: {
                type: Array,
                default: () => [
                    {text: 'Показывать по 25', value: 25},
                    {text: 'Показывать по 50', value: 50},
                    {text: 'Показывать по 100', value: 100},
                ]
            },
            arrowNavigation: {
                type: Boolean | null,
                default: true
            },
        },
        data() {
            return {
                perPageData: null,
                toTopActive: false,
                configuredTableFields: null,
                isTableFieldsChanged: false,
                configuratorModal: false,
                searchString: '',
                windowWidth: document.body.clientWidth,
                topButton: window.localStorage.getItem('is_top_button') === 'yes'
            }
        },
        local: ['configuredTableFields'],
        created() {
            window.addEventListener('resize', this.handleResize);
            this.$data.perPageData = this.$props.perPage;
            this.scrollListener();
            this.$root.$on('shortkey', this.shortkeyHandler)
        },
        destroyed(){
            this.$root.$off('shortkey', this.shortkeyHandler)
        },
        computed: {
            configuredTableFieldsActive() {
                let result = 0;
                for (let item of this.$data.configuredTableFields) {
                    if (item.show) {
                        result++;
                    }
                }
                return !(result > 1);
            }
        },
        mounted() {
            this.setFieldsData();
        },
        methods: {
            handleResize() {
                this.$data.windowWidth = document.body.clientWidth
            },
            setFieldsData() {
                if (this.$props.configurableTable && this.$props.tableFields) {
                    if (!Array.isArray(this.$data.configuredTableFields)) {
                        this.$data.configuredTableFields = [].concat(this.$props.tableFields);
                    } else if (this.$data.configuredTableFields.length !== this.$props.tableFields.length) {
                        this.$data.configuredTableFields = [].concat(this.$props.tableFields);
                    }
                    this.$emit('fields-setup', filter(this.$data.configuredTableFields, {show: true}));
                    this.$data.isTableFieldsChanged = this.hash(this.$props.tableFields) !== this.hash(this.$data.configuredTableFields);
                }
            },
            resetTableFields() {
                this.$data.configuredTableFields = this.$props.tableFields;
                this.$data.isTableFieldsChanged = this.hash(this.$props.tableFields) !== this.hash(this.$data.configuredTableFields);
                this.$emit('fields-changed', this.$props.tableFields);
                this.$modal.hide('table-configurator');
                this.$forceUpdate();
            },
            scrollListener() {
                window.addEventListener('scroll', event => {
                    this.$data.toTopActive = window.scrollY >= this.$parent.$el.offsetTop + 32;
                }, false);
            },
            toTop() {
                scrollToElement(this.$parent.$el, {
                    offset: -72,
                    ease: 'inOutQuint',
                    duration: 400
                });
            },

            discardFilters() {
                this.$emit('discard-filters');
            },

            openTableConfigurator() {
                this.$data.configuratorModal = true;
                this.$modal.show('table-configurator');
            },

            closeTableConfigurator() {
                this.$data.configuratorModal = false;
                this.$modal.hide('table-configurator');
            },

            saveTableConfig() {
                this.$data.isTableFieldsChanged = this.hash(this.$props.tableFields) !== this.hash(this.$data.configuredTableFields);
                this.$emit('fields-changed', filter(this.$data.configuredTableFields, {show: true}));
            },

            shortkeyHandler(event) {
                if (this.$props.arrowNavigation) {
                    switch (event.srcKey) {
                        case 'left':
                            this.changePage(this.$props.currentPage - 1);
                            break;
                        case 'right':
                            this.changePage(this.$props.currentPage + 1);
                            break;
                        case 'del':
                            this.$emit('action', 'delete')
                            event.stopPropagation();
                            event.preventDefault();
                            break;
                        default:
                            console.log(event);
                            break;
                    }
                }
            },

            setSearchQuery(query) {
                this.$emit('search-changed', query)
            },

            search(query) {
                this.$emit('search', query)
            },

            doAction(action) {
                this.$emit('action', action.name)
            },
            changePage(page) {
                page && this.$props.currentPage !== page && this.toTop();
                this.$emit('change-page', page);
            },
            toggleTopButtonState(){
                this.$data.topButton = !this.$data.topButton;
                window.localStorage.setItem('is_top_button', this.$data.topButton ? 'yes' : 'no')
            }
        },
        watch: {
            query() {
                this.changePage(1);
            },
            perPageData(perPage, prevPerPage) {
                prevPerPage && this.changePage(1);
                this.$emit('change-per-page', perPage);
            },
        }
    }
</script>

<style lang="scss">
    @import "../../scss/variables/variables";

    .handle {
        cursor: move;
    }

    .table-header {
        padding: .75rem;
        background: $white;
        border: 1px $table-border-color solid;
        margin-bottom: -1px;

        &-actions {
            display: flex;
            flex-flow: row wrap;
            align-items: center;
        }

        .labels {
            margin: 1rem -.75rem -.75rem;
            border-top: 1px solid $table-border-color;
            padding: .5rem 1rem;
            background: $gray-100;
        }
    }

    body:not(.low-performance){
        .table-header{
            @supports (backdrop-filter: blur(.8rem)) {
                background: rgba($white, .85);
                backdrop-filter: blur(1rem);
                box-shadow: 0 -1px 0 1px $white;
            }
        }
    }
</style>